import { inject, Injectable } from '@angular/core';
import { StorageKeys } from '@core/enums';
import { IDefaultService, ICategoryService, IDefaultServiceOption } from '@core/interface';
import { FdsCurrencyPipe } from 'apps/schedule/src/app/shared/pipes/currency/currency-status.pipe';
import { SessionService } from '../session/session.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})

export class GeneralServicesService {
  readonly #sessionService = inject(SessionService);
  readonly #currencyPipe = inject(FdsCurrencyPipe);
  readonly #translateService = inject(TranslateService);

  /**
   * Retorna os serviços disponíveis para uma categoria
   * @param categoryId ID da categoria
   * @returns Lista de serviços disponíveis para a categoria
   */
  getServicesByCategoryId(categoryId: number): IDefaultService[] {
    const categories = this.#sessionService.getObject<{ [key: number]: ICategoryService }>(StorageKeys.CATEGORIES_BY_DEALER);
    return categories[categoryId]?.availableServices;
  }

  /**
   * Salva os serviços selecionados em uma categoria
   * @param categoryId ID da categoria
   * @param savedServices lista atualizada dos serviços
   */
  setServicesByCategoryId(categoryId: number, savedServices: IDefaultService[]): void {
    console.log(savedServices);
    const categories = this.#sessionService.getObject<{ [key: number]: ICategoryService }>(StorageKeys.CATEGORIES_BY_DEALER);
    if (categories && categories[categoryId]) {
      categories[categoryId].availableServices = savedServices;
      this.#sessionService.setObject(StorageKeys.CATEGORIES_BY_DEALER, categories);
    }
  }

  /**
   * Retorna apenas o que precisamos para exibir os serviços na lista de opções selecionáveis
   * @param categoryId ID da categoria
   * @returns Lista de informações para exibição dos serviços de uma categoria
   */
  getFormattedServicesByCategoryId(categoryId: number): IDefaultServiceOption[] {
    const services = this.getServicesByCategoryId(categoryId);
    return services.map(service => ({
      id: service.serviceId,
      serviceName: service.serviceName,
      price: service.servicePrice,
      formattedPrice: this.#formatServicePrice(service),
      description: service.name,
      active: true,
      isCostless: service.costless,
      isSelected: this.#recoverIsSelectedFlag(categoryId, service.serviceId),
      isNeedComment: service.displayComment,
    }));
  }

  /**
   * Formata o preço de um serviço, seja com a currency ou com o texto gratuito/sob consulta
   * @param service serviço que será formatado
   * @returns O preço do serviço formatado
   */
  #formatServicePrice(service: IDefaultService): string {
    if (service.costless) {
      return this.#translateService.instant('general-services.price.free');
    }
    if (!service.servicePrice) {
      return this.#translateService.instant('general-services.price.on-request');
    }
    return this.#currencyPipe.transform(service.servicePrice);
  }

  /**
   * Salva em um array o Id dos serviços que temos selecionados
   * @param categoryId Id da categoria dos serviços, será usado como index do array
   * @param selectedServiceIds Ids dos serviços que estão selecionados (checked)
   */
  setSelectedServiceIds(categoryId: number, selectedServiceIds: number[]): void {
    const selectedServicesKey = '@Ford-selectedServicesByCategory';
    let selectedServicesByCategory = this.#sessionService.getObject<{ [key: number]: number[] }>(selectedServicesKey);
    if (!selectedServicesByCategory) {
      selectedServicesByCategory = {};
    }
    selectedServicesByCategory[categoryId] = selectedServiceIds;
    this.#sessionService.setObject(selectedServicesKey, selectedServicesByCategory);
  }

  /**
   * Retorna uma lista com os Ids dos serviços selecionados (checked) de uma categoria
   * @param categoryId o Id da categoria ao qual os serviços pertencem
   * @returns array com os Ids dos serviços selecionados
   */
  getSelectedServiceIdsByCategoryId(categoryId: number): number[] {
    const selectedServicesKey = '@Ford-selectedServicesByCategory';
    const selectedServicesByCategory = this.#sessionService.getObject<{ [key: number]: number[] }>(selectedServicesKey);
    return selectedServicesByCategory?.[categoryId] || [];
  }

  /**
   * Retorna todos os Ids dos serviços selecionados de todas as categorias
   * @returns array com os Ids dos serviços selecionados
   */
  getAllSelectedServices() {
    const selectedServicesKey = '@Ford-selectedServicesByCategory';
    return this.#sessionService.getObject<{ [key: number]: number[] }>(selectedServicesKey) || {};
  }

  /**
   * Limpa todos os serviços selecionados salvos em storage
   */
  clearAllSelectedServices(): void {
    const selectedServicesKey = '@Ford-selectedServicesByCategory';
    this.#sessionService.removeItem(selectedServicesKey);
  }

  /**
   * Retorna o valor de isChecked dependendo da existência do Id do serviço no array do storage
   * @param categoryId o Id da categoria ao qual o serviço pertence
   * @param serviceId o Id do serviço
   * @returns true se o Id do serviço existir nos checkedServices, false se não
   */
  #recoverIsSelectedFlag(categoryId: number, serviceId: number): boolean {
    const selectedServiceIds = this.getSelectedServiceIdsByCategoryId(categoryId);
    return selectedServiceIds.includes(serviceId);
  }
}
